import { IPublicClientApplication, AccountInfo } from '@azure/msal-browser';

export interface UserProfile {
  id: string;
  displayName: string;
  mail: string;
  userPrincipalName: string;
  jobTitle?: string;
  department?: string;
  officeLocation?: string;
  profilePicture?: string; // Base64 encoded image data
}

/**
 * Get an access token for Microsoft Graph API
 */
export const getGraphAccessToken = async (msalInstance: IPublicClientApplication): Promise<string | null> => {
  try {
    console.log('🔑 Getting access token...');
    
    // Check all accounts first
    const allAccounts = msalInstance.getAllAccounts();
    console.log('👥 All accounts:', allAccounts.length);
    allAccounts.forEach((acc, index) => {
      console.log(`  Account ${index}: ${acc.name} (${acc.username})`);
    });
    
    const account = msalInstance.getActiveAccount();
    console.log('👤 Active account:', account ? `${account.name} (${account.username})` : 'NULL');
    
    // If no active account but we have accounts, try to set the first one as active
    if (!account && allAccounts.length > 0) {
      console.log('🔄 No active account found, setting first account as active...');
      msalInstance.setActiveAccount(allAccounts[0]);
      const newActiveAccount = msalInstance.getActiveAccount();
      console.log('👤 New active account:', newActiveAccount ? `${newActiveAccount.name} (${newActiveAccount.username})` : 'NULL');
    }
    
    const finalAccount = msalInstance.getActiveAccount();
    if (!finalAccount) {
      console.error('❌ No active account found - user may not be logged in');
      return null;
    }

    console.log('🔄 Acquiring token silently...');
    const response = await msalInstance.acquireTokenSilent({
      scopes: ['User.Read', 'Mail.Read', 'User.Read.All'],
      account: finalAccount,
    });

    console.log('✅ Token acquired successfully');
    console.log('🔍 Token scopes:', response.scopes.join(', '));
    return response.accessToken;
  } catch (error) {
    console.error('❌ Error acquiring token:', error);
    
    // If silent token acquisition fails, try interactive (without forcing consent)
    try {
      console.log('🔄 Silent token acquisition failed, trying interactive...');
      const response = await msalInstance.acquireTokenRedirect({
        scopes: ['User.Read', 'Mail.Read', 'User.Read.All']
        // Removed prompt: 'consent' to allow token caching
      });
      console.log('✅ Interactive token acquisition initiated (redirect)');
      return null; // With redirect, we won't get the token immediately
    } catch (interactiveError) {
      console.error('❌ Interactive token acquisition also failed:', interactiveError);
      return null;
    }
  }
};

/**
 * Fetch user profile information from Microsoft Graph
 */
export const getUserProfile = async (msalInstance: IPublicClientApplication): Promise<UserProfile | null> => {
  try {
    console.log('👤 Fetching user profile...');
    
    const accessToken = await getGraphAccessToken(msalInstance);
    if (!accessToken) {
      console.log('❌ No access token available for user profile');
      return null;
    }

    console.log('✅ Access token obtained, fetching user profile...');
    const response = await fetch('https://graph.microsoft.com/v1.0/me', {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    console.log(`📡 User profile API response: ${response.status} ${response.statusText}`);

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`❌ User profile API error: ${response.status} - ${errorText}`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const userData = await response.json();
    console.log('✅ User profile data received:', {
      id: userData.id,
      displayName: userData.displayName,
      mail: userData.mail,
      userPrincipalName: userData.userPrincipalName
    });
    
    return {
      id: userData.id,
      displayName: userData.displayName,
      mail: userData.mail,
      userPrincipalName: userData.userPrincipalName,
      jobTitle: userData.jobTitle,
      department: userData.department,
      officeLocation: userData.officeLocation,
    };
  } catch (error) {
    console.error('❌ Error fetching user profile:', error);
    return null;
  }
};

/**
 * Fetch user profile picture from Microsoft Graph for a specific user
 * Returns base64 encoded image data
 */
export const getUserProfilePicture = async (msalInstance: IPublicClientApplication, userEmail?: string): Promise<string | null> => {
  try {
    console.log('🔍 Starting profile picture fetch...');
    
    const accessToken = await getGraphAccessToken(msalInstance);
    if (!accessToken) {
      console.log('❌ No access token available');
      return null;
    }
    
    console.log('✅ Access token obtained, making API call...');

    // Use specific user email if provided, otherwise use /me for current user
    const endpoint = userEmail 
      ? `https://graph.microsoft.com/v1.0/users/${userEmail}/photo/$value`
      : 'https://graph.microsoft.com/v1.0/me/photo/$value';
    
    console.log(`📡 Fetching profile picture from: ${endpoint}`);
    console.log(`📧 User email parameter: "${userEmail}"`);

    const response = await fetch(endpoint, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });

    console.log(`📡 API Response Status: ${response.status} ${response.statusText}`);
    console.log(`📡 Response Headers:`, Object.fromEntries(response.headers.entries()));

    if (!response.ok) {
      if (response.status === 404) {
        console.log('❌ No profile picture found for user (404)');
        return null;
      }
      if (response.status === 403) {
        console.log('❌ Access denied (403) - check permissions');
        return null;
      }
      console.log(`❌ HTTP error! status: ${response.status}`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    console.log('✅ Profile picture found, converting to base64...');

    // Convert the image to base64
    const arrayBuffer = await response.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);
    const base64 = btoa(String.fromCharCode.apply(null, Array.from(uint8Array)));
    
    // Determine the content type from the response
    const contentType = response.headers.get('content-type') || 'image/jpeg';
    
    console.log(`✅ Profile picture converted successfully. Size: ${uint8Array.length} bytes, Type: ${contentType}`);
    
    return `data:${contentType};base64,${base64}`;
  } catch (error) {
    console.error('❌ Error fetching profile picture:', error);
    return null;
  }
};

/**
 * Get user profile picture URL (alternative method)
 * This returns a URL that can be used directly in img src
 */
export const getUserProfilePictureUrl = async (msalInstance: IPublicClientApplication): Promise<string | null> => {
  try {
    const accessToken = await getGraphAccessToken(msalInstance);
    if (!accessToken) {
      return null;
    }

    // First, check if the user has a profile picture
    const metadataResponse = await fetch('https://graph.microsoft.com/v1.0/me/photo', {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!metadataResponse.ok) {
      return null;
    }

    // Return the URL for the profile picture
    return `https://graph.microsoft.com/v1.0/me/photo/$value`;
  } catch (error) {
    console.error('Error getting profile picture URL:', error);
    return null;
  }
};

/**
 * Create a data URL for profile picture with access token
 * This method embeds the access token in the URL for direct use
 */
export const createProfilePictureDataUrl = async (msalInstance: IPublicClientApplication): Promise<string | null> => {
  try {
    const accessToken = await getGraphAccessToken(msalInstance);
    if (!accessToken) {
      return null;
    }

    // Create a URL with the access token
    return `https://graph.microsoft.com/v1.0/me/photo/$value?access_token=${accessToken}`;
  } catch (error) {
    console.error('Error creating profile picture data URL:', error);
    return null;
  }
};

/**
 * Alternative method to get profile picture using different endpoints
 * This tries multiple approaches including Exchange Online and other sources
 */
export const getUserProfilePictureAlternative = async (msalInstance: IPublicClientApplication, userEmail?: string): Promise<string | null> => {
  try {
    console.log('🔄 Trying alternative profile picture methods...');
    
    const accessToken = await getGraphAccessToken(msalInstance);
    if (!accessToken) {
      console.log('❌ No access token available');
      return null;
    }

    // Try different endpoints including Exchange Online and other sources
    const baseEndpoints = userEmail 
      ? [
          // Entra ID endpoints for specific user
          `https://graph.microsoft.com/v1.0/users/${userEmail}/photo/$value`,
          `https://graph.microsoft.com/beta/users/${userEmail}/photo/$value`,
          `https://graph.microsoft.com/v1.0/users/${userEmail}/photos/48x48/$value`,
          `https://graph.microsoft.com/v1.0/users/${userEmail}/photos/64x64/$value`,
          `https://graph.microsoft.com/v1.0/users/${userEmail}/photos/96x96/$value`,
          `https://graph.microsoft.com/v1.0/users/${userEmail}/photos/120x120/$value`,
          `https://graph.microsoft.com/v1.0/users/${userEmail}/photos/240x240/$value`,
          `https://graph.microsoft.com/v1.0/users/${userEmail}/photos/360x360/$value`,
          `https://graph.microsoft.com/v1.0/users/${userEmail}/photos/432x432/$value`,
          `https://graph.microsoft.com/v1.0/users/${userEmail}/photos/504x504/$value`,
          `https://graph.microsoft.com/v1.0/users/${userEmail}/photos/648x648/$value`,
          
          // Exchange Online endpoints for specific user
          `https://graph.microsoft.com/v1.0/users/${userEmail}/mailboxSettings/photo/$value`,
          `https://graph.microsoft.com/beta/users/${userEmail}/mailboxSettings/photo/$value`,
        ]
      : [
          // Entra ID endpoints for current user
          'https://graph.microsoft.com/v1.0/me/photo/$value',
          'https://graph.microsoft.com/beta/me/photo/$value',
          'https://graph.microsoft.com/v1.0/me/photos/48x48/$value',
          'https://graph.microsoft.com/v1.0/me/photos/64x64/$value',
          'https://graph.microsoft.com/v1.0/me/photos/96x96/$value',
          'https://graph.microsoft.com/v1.0/me/photos/120x120/$value',
          'https://graph.microsoft.com/v1.0/me/photos/240x240/$value',
          'https://graph.microsoft.com/v1.0/me/photos/360x360/$value',
          'https://graph.microsoft.com/v1.0/me/photos/432x432/$value',
          'https://graph.microsoft.com/v1.0/me/photos/504x504/$value',
          'https://graph.microsoft.com/v1.0/me/photos/648x648/$value',
          
          // Exchange Online endpoints for current user
          'https://graph.microsoft.com/v1.0/me/mailboxSettings/photo/$value',
          'https://graph.microsoft.com/beta/me/mailboxSettings/photo/$value',
          
          // User profile endpoints
          'https://graph.microsoft.com/v1.0/me/profile/photo/$value',
          'https://graph.microsoft.com/beta/me/profile/photo/$value',
          
          // SharePoint/OneDrive endpoints (sometimes used for profile pictures)
          'https://graph.microsoft.com/v1.0/me/drive/root:/profile.jpg:/content',
          'https://graph.microsoft.com/v1.0/me/drive/root:/profile.png:/content',
          'https://graph.microsoft.com/v1.0/me/drive/root:/profile.jpeg:/content'
        ];
    
    const endpoints = baseEndpoints;

    for (const endpoint of endpoints) {
      try {
        console.log(`🔍 Trying endpoint: ${endpoint}`);
        
        const response = await fetch(endpoint, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        });

        console.log(`📡 Response for ${endpoint}: ${response.status} ${response.statusText}`);

        if (response.ok) {
          console.log(`✅ Success with endpoint: ${endpoint}`);
          
          // Convert the image to base64
          const arrayBuffer = await response.arrayBuffer();
          const uint8Array = new Uint8Array(arrayBuffer);
          const base64 = btoa(String.fromCharCode.apply(null, Array.from(uint8Array)));
          
          // Determine the content type from the response
          const contentType = response.headers.get('content-type') || 'image/jpeg';
          
          console.log(`✅ Profile picture converted successfully. Size: ${uint8Array.length} bytes, Type: ${contentType}`);
          console.log(`🔍 Picture data hash (first 20 chars): ${base64.substring(0, 20)}...`);
          
          return `data:${contentType};base64,${base64}`;
        } else if (response.status === 404) {
          console.log(`❌ No picture at ${endpoint} (404)`);
          continue;
        } else {
          console.log(`❌ Error at ${endpoint}: ${response.status}`);
          continue;
        }
      } catch (error) {
        console.log(`❌ Exception at ${endpoint}:`, error);
        continue;
      }
    }

    console.log('❌ No profile picture found at any endpoint');
    return null;
  } catch (error) {
    console.error('❌ Error in alternative profile picture fetch:', error);
    return null;
  }
};

/**
 * Get profile picture from Exchange Online (where Office 365 profile pictures are often stored)
 * This requires Mail.Read or User.Read permissions
 */
export const getExchangeProfilePicture = async (msalInstance: IPublicClientApplication, userEmail?: string): Promise<string | null> => {
  try {
    console.log(`📧 Trying Exchange Online profile picture for ${userEmail || 'current user'}...`);
    console.log(`📧 User email parameter: "${userEmail}"`);
    
    const accessToken = await getGraphAccessToken(msalInstance);
    if (!accessToken) {
      console.log('❌ No access token available');
      return null;
    }

    // Try Exchange Online specific endpoints
    const baseEndpoints = userEmail 
      ? [
          `https://graph.microsoft.com/v1.0/users/${userEmail}/photo/$value`,
          `https://graph.microsoft.com/beta/users/${userEmail}/photo/$value`,
          `https://graph.microsoft.com/v1.0/users/${userEmail}/mailboxSettings/photo/$value`,
          `https://graph.microsoft.com/beta/users/${userEmail}/mailboxSettings/photo/$value`
        ]
      : [
          'https://graph.microsoft.com/v1.0/me/photo/$value',
          'https://graph.microsoft.com/beta/me/photo/$value',
          'https://graph.microsoft.com/v1.0/me/mailboxSettings/photo/$value',
          'https://graph.microsoft.com/beta/me/mailboxSettings/photo/$value'
        ];
    
    const exchangeEndpoints = baseEndpoints;

    for (const endpoint of exchangeEndpoints) {
      try {
        console.log(`📧 Trying Exchange endpoint: ${endpoint}`);
        
        const response = await fetch(endpoint, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        });

        console.log(`📡 Exchange response: ${response.status} ${response.statusText}`);

        if (response.ok) {
          console.log(`✅ Exchange profile picture found at: ${endpoint}`);
          
          // Convert the image to base64
          const arrayBuffer = await response.arrayBuffer();
          const uint8Array = new Uint8Array(arrayBuffer);
          const base64 = btoa(String.fromCharCode.apply(null, Array.from(uint8Array)));
          
          // Determine the content type from the response
          const contentType = response.headers.get('content-type') || 'image/jpeg';
          
          console.log(`✅ Exchange profile picture converted successfully. Size: ${uint8Array.length} bytes, Type: ${contentType}`);
          console.log(`🔍 Picture data hash (first 20 chars): ${base64.substring(0, 20)}...`);
          
          return `data:${contentType};base64,${base64}`;
        } else if (response.status === 404) {
          console.log(`❌ No Exchange picture at ${endpoint} (404)`);
          continue;
        } else {
          console.log(`❌ Exchange error at ${endpoint}: ${response.status}`);
          continue;
        }
      } catch (error) {
        console.log(`❌ Exchange exception at ${endpoint}:`, error);
        continue;
      }
    }

    console.log('❌ No Exchange profile picture found');
    return null;
  } catch (error) {
    console.error('❌ Error in Exchange profile picture fetch:', error);
    return null;
  }
};

/**
 * Test different scope combinations to see which one works
 */
export const testScopes = async (msalInstance: IPublicClientApplication): Promise<void> => {
  const scopeCombinations = [
    ['User.Read'],
    ['User.Read', 'Mail.Read'],
    ['User.ReadBasic.All'],
    ['User.Read.All'],
    ['openid', 'profile', 'email'],
    ['openid', 'profile', 'email', 'User.Read']
  ];

  for (const scopes of scopeCombinations) {
    try {
      console.log(`🧪 Testing scopes: ${scopes.join(', ')}`);
      
      const account = msalInstance.getActiveAccount();
      if (!account) {
        console.log('❌ No active account for scope test');
        continue;
      }

      const response = await msalInstance.acquireTokenSilent({
        scopes: scopes,
        account: account,
      });

      console.log(`✅ Token acquired with scopes: ${scopes.join(', ')}`);

      // Test the /me endpoint with this token
      const apiResponse = await fetch('https://graph.microsoft.com/v1.0/me', {
        headers: {
          'Authorization': `Bearer ${response.accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      console.log(`📡 API response with ${scopes.join(', ')}: ${apiResponse.status} ${apiResponse.statusText}`);

      if (apiResponse.ok) {
        const userData = await apiResponse.json();
        console.log(`✅ SUCCESS with scopes ${scopes.join(', ')}: ${userData.displayName}`);
        return; // Found working scopes
      } else {
        const errorText = await apiResponse.text();
        console.log(`❌ API failed with ${scopes.join(', ')}: ${apiResponse.status} - ${errorText}`);
      }

    } catch (error) {
      console.log(`❌ Scope test failed for ${scopes.join(', ')}:`, error);
      
      // If silent token acquisition fails, try interactive with consent
      try {
        console.log(`🔄 Trying interactive consent for scopes: ${scopes.join(', ')}`);
        const interactiveResponse = await msalInstance.acquireTokenRedirect({
          scopes: [...scopes, 'User.Read.All'],
          prompt: 'consent'
        });
        
        console.log(`✅ Interactive consent initiated for scopes: ${scopes.join(', ')} (redirect)`);
        return; // With redirect, we can't continue testing immediately
      } catch (interactiveError) {
        console.log(`❌ Interactive consent failed for ${scopes.join(', ')}:`, interactiveError);
      }
    }
  }
};
